/**
 * ActionTypeTag Components
 * Tags for displaying action types (email, message, task, meeting)
 *
 * Three variants:
 * - IconBadge: Small square with just the icon (28x28)
 * - IconBadgeWithCount: Pill with icon and count number (e.g., icon + "3")
 * - Badge: Pill with icon and text label (e.g., icon + "Message")
 *
 * All variants support an optional `template` prop that shows a small "T"
 * indicator at the bottom-right corner and appends "Template" to the label/tooltip.
 */

import { Box, HStack, Icon, Text, Tooltip } from "@chakra-ui/react";
import CaminoCheckpointIcon from "design-system/icons/Checkpoint";
import CaminoEmailIcon from "design-system/icons/Email";
import CaminoFormIcon from "design-system/icons/Form";
import CaminoMeetingIcon from "design-system/icons/Meeting";
import CaminoMessageIcon from "design-system/icons/Message";
import CaminoTaskIcon from "design-system/icons/Task";
import { TagSize, ICON_BADGE_SIZE_STYLES, PILL_BADGE_SIZE_STYLES } from "design-system/foundation/TagSizeStyles";
import { AdminMeetingTypeTag, MeetingTypeInput } from "design-system/components/AdminMeetingTypeTag";

const ICON_COLOR = "actionType.icon";
const BG_COLOR = "actionType.bg";

const ACTION_CONFIG = {
  checkpoint: { label: "Checkpoint", icon: CaminoCheckpointIcon, iconScale: 1 },
  email: { label: "Email", icon: CaminoEmailIcon, iconScale: 1 },
  form: { label: "Form", icon: CaminoFormIcon, iconScale: 1 },
  message: { label: "Message", icon: CaminoMessageIcon, iconScale: 1 },
  task: { label: "Task", icon: CaminoTaskIcon, iconScale: 1 },
  meeting: { label: "Meeting", icon: CaminoMeetingIcon, iconScale: 1 },
} as const;

export type ActionTypeKey = keyof typeof ACTION_CONFIG;

// Base components

interface ActionIconBadgeProps {
  actionType: ActionTypeKey;
  size?: TagSize;
}

function ActionIconBadge({ actionType, size = "md" }: ActionIconBadgeProps) {
  const config = ACTION_CONFIG[actionType];
  const styles = ICON_BADGE_SIZE_STYLES[size];
  const iconPx = parseFloat(styles.iconSize) * config.iconScale;
  const scaledIconSize = `${iconPx}px`;
  return (
    <Tooltip label={config.label} fontSize="xs">
      <Box as="span" position="relative" display="inline-block">
        <Box
          as="span"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          w={styles.w}
          h={styles.h}
          p={styles.p}
          bg={BG_COLOR}
          borderRadius={styles.borderRadius}
        >
          <Icon as={config.icon} boxSize={scaledIconSize} color={ICON_COLOR} />
        </Box>
      </Box>
    </Tooltip>
  );
}

interface ActionIconBadgeWithCountProps {
  actionType: ActionTypeKey;
  count: number;
  size?: TagSize;
}

function ActionIconBadgeWithCount({ actionType, count, size = "md" }: ActionIconBadgeWithCountProps) {
  const config = ACTION_CONFIG[actionType];
  const styles = PILL_BADGE_SIZE_STYLES[size];
  const iconPx = parseFloat(styles.iconSize) * config.iconScale;
  const scaledIconSize = `${iconPx}px`;
  return (
    <Tooltip label={config.label} fontSize="xs">
      <Box as="span" position="relative" display="inline-block">
        <HStack
          as="span"
          display="inline-flex"
          spacing={styles.spacing}
          h={styles.h}
          px={styles.px}
          py={styles.py}
          bg={BG_COLOR}
          borderRadius={styles.borderRadius}
        >
          <Icon as={config.icon} boxSize={scaledIconSize} color={ICON_COLOR} />
          <Text
            fontSize={styles.fontSize}
            fontWeight="600"
            lineHeight={styles.lineHeight}
            letterSpacing="0px"
            color={ICON_COLOR}
          >
            {count}
          </Text>
        </HStack>
      </Box>
    </Tooltip>
  );
}

interface ActionBadgeProps {
  actionType: ActionTypeKey;
  size?: TagSize;
}

function ActionBadge({ actionType, size = "md" }: ActionBadgeProps) {
  const config = ACTION_CONFIG[actionType];
  const styles = PILL_BADGE_SIZE_STYLES[size];
  const iconPx = parseFloat(styles.iconSize) * config.iconScale;
  const scaledIconSize = `${iconPx}px`;
  return (
    <Box as="span" position="relative" display="inline-block">
      <HStack
        as="span"
        display="inline-flex"
        spacing={styles.spacing}
        h={styles.h}
        px={styles.px}
        py={styles.py}
        bg={BG_COLOR}
        borderRadius={styles.borderRadius}
      >
        <Icon as={config.icon} boxSize={scaledIconSize} color={ICON_COLOR} />
        <Text
          fontSize={styles.fontSize}
          fontWeight="600"
          lineHeight={styles.lineHeight}
          letterSpacing="0px"
          color={ICON_COLOR}
          whiteSpace="nowrap"
        >
          {config.label}
        </Text>
      </HStack>
    </Box>
  );
}

// Checkpoint
function CheckpointIconBadge({ size }: { size?: TagSize }) {
  return <ActionIconBadge actionType="checkpoint" size={size} />;
}

function CheckpointIconBadgeWithCount({ count, size }: { count: number; size?: TagSize }) {
  return <ActionIconBadgeWithCount actionType="checkpoint" count={count} size={size} />;
}

function CheckpointBadge({ size }: { size?: TagSize }) {
  return <ActionBadge actionType="checkpoint" size={size} />;
}

// Email
function EmailIconBadge({ size }: { size?: TagSize }) {
  return <ActionIconBadge actionType="email" size={size} />;
}

function EmailIconBadgeWithCount({ count, size }: { count: number; size?: TagSize }) {
  return <ActionIconBadgeWithCount actionType="email" count={count} size={size} />;
}

function EmailBadge({ size }: { size?: TagSize }) {
  return <ActionBadge actionType="email" size={size} />;
}

// Message
function MessageIconBadge({ size }: { size?: TagSize }) {
  return <ActionIconBadge actionType="message" size={size} />;
}

function MessageIconBadgeWithCount({ count, size }: { count: number; size?: TagSize }) {
  return <ActionIconBadgeWithCount actionType="message" count={count} size={size} />;
}

function MessageBadge({ size }: { size?: TagSize }) {
  return <ActionBadge actionType="message" size={size} />;
}

// Task
function TaskIconBadge({ size }: { size?: TagSize }) {
  return <ActionIconBadge actionType="task" size={size} />;
}

function TaskIconBadgeWithCount({ count, size }: { count: number; size?: TagSize }) {
  return <ActionIconBadgeWithCount actionType="task" count={count} size={size} />;
}

function TaskBadge({ size }: { size?: TagSize }) {
  return <ActionBadge actionType="task" size={size} />;
}

// Meeting
function MeetingIconBadge({ size }: { size?: TagSize }) {
  return <ActionIconBadge actionType="meeting" size={size} />;
}

function MeetingIconBadgeWithCount({ count, size }: { count: number; size?: TagSize }) {
  return <ActionIconBadgeWithCount actionType="meeting" count={count} size={size} />;
}

function MeetingBadge({ size }: { size?: TagSize }) {
  return <ActionBadge actionType="meeting" size={size} />;
}

interface ActionIconBadgeWithMeetingTypeProps {
  actionType: ActionTypeKey;
  meetingType?: MeetingTypeInput;
  size?: TagSize;
}

function ActionIconBadgeWithMeetingType({ actionType, meetingType, size = "md" }: ActionIconBadgeWithMeetingTypeProps) {
  if (actionType === "meeting" && meetingType) {
    return <AdminMeetingTypeTag.IconBadge meetingType={meetingType} size={size} />;
  }

  return <ActionIconBadge actionType={actionType} size={size} />;
}

// Form convenience wrappers — for parity with the other action types.
function FormIconBadge({ size }: { size?: TagSize }) {
  return <ActionIconBadge actionType="form" size={size} />;
}
function FormIconBadgeWithCount({ count, size }: { count: number; size?: TagSize }) {
  return <ActionIconBadgeWithCount actionType="form" count={count} size={size} />;
}
function FormBadge({ size }: { size?: TagSize }) {
  return <ActionBadge actionType="form" size={size} />;
}

// Public API
export const ActionTypeTag = {
  // Dynamic - accepts actionType prop
  IconBadge: ActionIconBadge,
  IconBadgeWithCount: ActionIconBadgeWithCount,
  IconBadgeWithMeetingType: ActionIconBadgeWithMeetingType,
  Badge: ActionBadge,
  // Checkpoint
  CheckpointIconBadge,
  CheckpointIconBadgeWithCount,
  CheckpointBadge,
  // Email
  EmailIconBadge,
  EmailIconBadgeWithCount,
  EmailBadge,
  // Form
  FormIconBadge,
  FormIconBadgeWithCount,
  FormBadge,
  // Message
  MessageIconBadge,
  MessageIconBadgeWithCount,
  MessageBadge,
  // Task
  TaskIconBadge,
  TaskIconBadgeWithCount,
  TaskBadge,
  // Meeting
  MeetingIconBadge,
  MeetingIconBadgeWithCount,
  MeetingBadge,
};
